import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Typography,
  Button,
  Tag,
  Progress,
  Statistic,
  Row,
  Col,
  Space,
  Alert,
  Descriptions,
  message,
} from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import Sidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import {
  getBulkResultEndpoint,
  getBulkDownloadEndpoint,
  authHeaders,
  bulkStatusColor,
  isTerminalBulkStatus,
} from './bulkVerificationApi';
import './Bounce.css';
import { decryptData } from '../middleware/encryption';

const { Content } = Layout;
const { Title, Text } = Typography;

const POLL_INTERVAL_MS = 5000;

const BulkResultPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef(null);

  const fetchJob = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const res = await fetch(getBulkResultEndpoint(id), { headers: authHeaders() });
        const result = await res.json();

        console.log('Fetched bulk job result:', decryptData(result.data));
        
        setJob(decryptData(result.data).data);
      } catch (err) {
        setError(err.message || 'Failed to fetch job result');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  // Auto-refresh while the job is still running; stop once it's done/failed.
  useEffect(() => {
    if (job && !isTerminalBulkStatus(job.status)) {
      pollRef.current = setInterval(() => fetchJob({ silent: true }), POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [job, fetchJob]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(getBulkDownloadEndpoint(id), { headers: authHeaders() });
      if (!res.ok) {
        const result = await res.json().catch(() => null);
        throw new Error(result?.message || 'Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${job?.file_name ? job.file_name.replace(/\.[^/.]+$/, '') : 'result'}_validated.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error(err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const progress = job?.total_emails ? Math.round((job.processed_emails / job.total_emails) * 100) : 0;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />
      <Layout>
        <AppHeader />
        <Content className="bounce-page" style={{ padding: 20 }}>
          <Button onClick={() => navigate(-1)} style={{ marginBottom: 20 }} icon={<ArrowLeftOutlined />}>
            Back
          </Button>

          <Card
            className="bounce-card"
            loading={loading}
            title={
              <Title level={3} style={{ margin: 0 }}>
                Bulk Verification Result
              </Title>
            }
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={() => fetchJob()}>
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  loading={downloading}
                  disabled={!job || job.processed_emails === 0}
                >
                  Download Result
                </Button>
              </Space>
            }
          >
            {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

            {job && (
              <>
                <Descriptions column={2} bordered size="small" style={{ marginBottom: 24 }}>
                  <Descriptions.Item label="File Name">{job.file_name}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={bulkStatusColor(job.status)}>{job.status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Uploaded">
                    {job.created_at ? new Date(job.created_at).toLocaleString() : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Completed">
                    {job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}
                  </Descriptions.Item>
                </Descriptions>

                <Progress
                  percent={progress}
                  status={job.status === 'failed' ? 'exception' : undefined}
                  style={{ marginBottom: 24 }}
                />

                <Row gutter={16}>
                  <Col span={4}>
                    <Statistic title="Total" value={job.total_emails} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="Processed" value={job.processed_emails} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="Valid" value={job.valid_count} valueStyle={{ color: '#3f8600' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="Invalid" value={job.invalid_count} valueStyle={{ color: '#cf1322' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="Risky" value={job.risky_count} valueStyle={{ color: '#d48806' }} />
                  </Col>
                  <Col span={4}>
                    <Statistic title="Unknown" value={job.unknown_count} />
                  </Col>
                </Row>

                {!isTerminalBulkStatus(job.status) && (
                  <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                    This job is still processing — the page refreshes automatically every{' '}
                    {POLL_INTERVAL_MS / 1000}s.
                  </Text>
                )}

                {job.error_message && (
                  <Alert message={job.error_message} type="error" showIcon style={{ marginTop: 16 }} />
                )}
              </>
            )}
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default BulkResultPage;